import * as React from 'react';
import {TypeImplementation} from '@packages/systems/wfdl';
import {TText, EText} from '@packages/systems/wfdl-ast';
import {
  ExpressionEditor,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import TextInput from '@packages/systems/design-system/TextInput';
import {styled, emotionStyled} from '@packages/utilities/styled';
import {Note, Select as SpringSelect, tokens} from '@packages/systems/spring';
import {
  isBoundElement,
  isInsideRepeaterWithImageRefSet,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {
  selectCurrentComponent,
  selectSelectedElement,
} from '@packages/utilities/platform-selectors';
import {isDynamoImgBinding} from '@packages/systems/dynamo/wfdl';

import {
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const NoteWithColumn = styled(Note)(() => ({
  gridColumn: '1 / -1',
}));

const Separator = styled('div')(() => ({
  width: '100%',
  height: `8px`,
}));

const dropdownValues = {
  __wf_reserved_inherit: 'Use alt text from asset',
  __wf_reserved_decorative: 'Decorative',
  custom: 'Custom description',
} as const;

// These same keys are still also used in the Legacy Image and Figure Settings
// ImageSettingsShared.tsx and ContentEditorFigureSettingsModal.tsx. If any changes
// are made here make sure to duplicate them there.
const reservedKeys = new Set([
  '__wf_reserved_inherit',
  '__wf_reserved_decorative',
]);

export const AltText = TypeImplementation.Alias({
  type: TText,
  system: {
    defaultValue: '__wf_reserved_inherit',
    displayName: 'Alt Text',
  },
  render: function AltTextPluginRender({expr, dispatch}) {
    const altText = ContextualExpressions.getText(stripAnn(expr));
    const altIsCustom = !(altText && reservedKeys.has(altText));
    const dropdownValue = altIsCustom ? 'custom' : altText || '';
    const [inputValue, setInputValue] = React.useState(
      altText && altIsCustom ? altText : ''
    );
    const [hasError, setHasError] = React.useState(false);

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (reservedKeys.has(value)) {
        setHasError(true);
      } else {
        setHasError(false);
      }
      setInputValue(e.target.value);
    };

    const onInputBlur = () => {
      if (!hasError) {
        dispatch(
          updateExpressionAction(EText(inputValue), {
            analytics: {type: 'element-settings-updated', value: 'alt text'},
          })
        );
      }
    };
    const onDropdownChange = (value: string) => {
      const newAlt = value === 'custom' ? '' : value;
      setHasError(false);
      setInputValue('');
      dispatch(
        updateExpressionAction(EText(newAlt), {
          analytics: {type: 'element-settings-updated', value: 'alt text'},
        })
      );
    };

    const currentComponent = useDesignerSelector(selectCurrentComponent);
    const selectedElement = useDesignerSelector(selectSelectedElement);

    const MultiImageAltNoteWrapper = emotionStyled('div')({
      marginBottom: tokens.space[2],
    });

    if (
      selectedElement &&
      (isDynamoImgBinding(selectedElement.data) ||
        isBoundElement(selectedElement, 'src')) &&
      currentComponent &&
      isInsideRepeaterWithImageRefSet(selectedElement)(currentComponent.render)
    ) {
      return (
        <MultiImageAltNoteWrapper data-automation-id="multi-image-alt-note">
          <Note colorVariant="default" hasIcon={false}>
            {'You can update alt text for multi-image field images in the CMS.'}
          </Note>
        </MultiImageAltNoteWrapper>
      );
    }

    if (altText === null) {
      return <ExpressionEditor expression={expr} dispatch={dispatch} />;
    }

    const altTextTypeOptions = Object.keys(dropdownValues).map((value) => ({
      value,
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly __wf_reserved_inherit: "Use alt text from asset"; readonly __wf_reserved_decorative: "Decorative"; readonly custom: "Custom description"; }'.
      label: dropdownValues[value],
    }));

    return (
      <>
        <div>
          <SpringSelect
            _shouldUseLegacyAlignment
            data-automation-id="AltTextPluginDropdown"
            id="AltTextPlugin"
            onChange={onDropdownChange}
            options={altTextTypeOptions}
            shouldPositionInline
            value={dropdownValue}
          />
          {dropdownValue === 'custom' && inputValue !== null ? (
            <>
              <Separator />
              <TextInput
                type="text"
                data-automation-id="AltTextPluginInput"
                placeholder="A cute hedgehog curled into a ball"
                value={inputValue}
                onBlur={onInputBlur}
                onChange={onInputChange}
                invalid={hasError}
              />
            </>
          ) : null}
        </div>
        {hasError ? (
          <NoteWithColumn hasIcon={false} colorVariant="danger">
            This is a reserved value.
          </NoteWithColumn>
        ) : null}
        {dropdownValue === 'custom' && inputValue === '' ? (
          <NoteWithColumn hasIcon={false} colorVariant="warning">
            Enter an alternative text description of this image for screen
            readers or if the image fails to load.
          </NoteWithColumn>
        ) : dropdownValue === '__wf_reserved_decorative' ? (
          <NoteWithColumn hasIcon={false} colorVariant="default">
            A decorative image has no semantic meaning, or has limited
            information value.
          </NoteWithColumn>
        ) : null}
      </>
    );
  },
});
